import { useState } from "react";
import { useT } from "../i18n";
import { Blattvonunten } from "./Blattvonunten";
import { Suchfeld, Zeilenblatt } from "./Handyformen";
import { Zeichen } from "./Zeichen";
import "../stile/telefon/suchfilter.css";

/* ══════════════════════════════════════════════════════════════════
   Suchen und filtern am Telefon — ein Feld, ein Knopf, ein Blatt.

   Luca, 19.09.2026, über Vermieten am Telefon:

   > *„Weil du oben den Typ Kunde wählen und dann diese ganzen Pilze
   > dazwischen hast. Das musst du irgendwie anders machen mit dem Filtern.
   > Mach einfach die Suchleiste so, wie sie ist, und dann hast du rechts
   > daneben so einen Filterbutton, und dann kommt ein Bottom Sheet mit den
   > möglichen Typen."*

   Und über das Nachschlagen: *„Da musst du diese Suchobjekte dann in ein
   Share Component machen und das da genauso sauber machen, sodass es dann
   ein zweites Bottom Sheet hochgeht."*

   ── Der Befund ─────────────────────────────────────────────────────

   Vermieten zeigte zwischen Suchfeld und Kundenzeile acht Gerätegruppen als
   Pillen in drei Reihen — gemessen 290 px auf 390 × 844, bevor die erste
   Maschine kam. Das Nachschlagen dasselbe, nur zugeklappt auf „Alle
   Gerätegruppen“ als eigene Zeile. Die Gruppen waren der Einstieg ohne
   Wort; als Wand zwischen Frage und Antwort standen sie im Weg.

   ── Die Form ──────────────────────────────────────────────────────

   | Teil | was es ist |
   |---|---|
   | das Feld | `Suchfeld` (`Handyformen.tsx`), unverändert |
   | der Knopf | 48 × 48, der Trichter; gesetzt: gelbe Kante und ein Punkt |
   | die Anzeige | **ein** Chip unter der Zeile, nur wenn gefiltert ist — mit × |
   | das Blatt | `Blattvonunten`, die Wahl als Zeilen: „Alle …“ und je Gruppe eine, mit Zahl und Haken |

   **Die Wahl gilt mit dem Tipp** und schließt das Blatt — eine Liste, aus
   der man eins nimmt, braucht kein „Übernehmen“. **Der Chip bleibt
   stehen**, weil ein Filter, den man nicht sieht, ein Fehler ist, den man
   nicht findet: „Warum steht der Radlader nicht da?“ Er ist einer, keine
   Reihe.

   Nur am Telefon. Der Schreibtisch behält seinen Pillenkranz. Klassen
   `sf-*` (`stile/telefon/suchfilter.css`).
   ══════════════════════════════════════════════════════════════════ */

/** Eine Wahl im Filterblatt — heute die Gerätegruppe (`Gruppenpille`). */
export type Filterwahl = { id: string; name: string; zahl: number };

export function Suchfilter({
  wert, aendern, platz, schild, wahl, gewaehlt, waehlen, filterTitel, alleWort,
}: {
  /** Das Suchwort und was beim Tippen geschieht — wie am `Suchfeld`. */
  wert: string;
  aendern: (neu: string) => void;
  platz: string;
  schild: string;
  /** Was sich wählen lässt. Ohne Einträge steht der Knopf gesperrt da. */
  wahl: Filterwahl[];
  /** Die gewählte `id` — `null` heißt: nicht gefiltert. */
  gewaehlt: string | null;
  waehlen: (id: string | null) => void;
  /** Worüber gefiltert wird — Titel des Blatts und Name des Knopfs („Gerätegruppe“). */
  filterTitel: string;
  /** Die erste Zeile im Blatt, die den Filter aufhebt („Alle Gerätegruppen“). */
  alleWort: string;
}) {
  const t = useT();
  const [offen, setzeOffen] = useState(false);
  const dran = wahl.find((w) => w.id === gewaehlt) ?? null;

  const nehmen = (id: string | null) => {
    waehlen(id);
    setzeOffen(false);
  };

  return (
    <div className="sf">
      <div className="sf-zeile">
        <Suchfeld wert={wert} aendern={aendern} platz={platz} schild={schild} />
        <button
          type="button"
          className="sf-knopf"
          aria-haspopup="dialog"
          aria-expanded={offen}
          aria-label={dran
            ? t("suchfilter.knopfGesetzt", { was: filterTitel, name: dran.name })
            : t("suchfilter.knopf", { was: filterTitel })}
          title={dran
            ? t("suchfilter.knopfGesetzt", { was: filterTitel, name: dran.name })
            : t("suchfilter.knopf", { was: filterTitel })}
          data-gesetzt={dran ? "ja" : undefined}
          disabled={wahl.length === 0 && !dran}
          onClick={() => setzeOffen(true)}
        >
          <Zeichen name="filter" />
          {dran ? <span className="sf-knopf-punkt" aria-hidden="true" /> : null}
        </button>
      </div>

      {/* Der gesetzte Filter, einer — und mit einem Tipp wieder weg. */}
      {dran ? (
        <div className="sf-gesetzt">
          {/* Die Pille des Hauses in ihrer gewählten Form, mit dem × jeder
              gesetzten Pille (`basis.css`, „Die Pille“). */}
          <button
            type="button"
            className="chip"
            aria-pressed="true"
            aria-label={t("suchfilter.weg", { name: dran.name })}
            onClick={() => waehlen(null)}
          >
            {dran.name} <span className="zahl">{dran.zahl}</span>
            <span className="chip-zu" aria-hidden="true">×</span>
          </button>
        </div>
      ) : null}

      <Blattvonunten
        offen={offen}
        schliessen={() => setzeOffen(false)}
        titel={filterTitel}
      >
        <Zeilenblatt>
          <button
            type="button"
            className="sf-wahl"
            aria-pressed={gewaehlt === null}
            onClick={() => nehmen(null)}
          >
            <span className="sf-wahl-name">{alleWort}</span>
            <span className="sf-wahl-haken">{gewaehlt === null ? <Zeichen name="haken" /> : null}</span>
          </button>
          {wahl.map((w) => (
            <button
              key={w.id}
              type="button"
              className="sf-wahl"
              aria-pressed={gewaehlt === w.id}
              onClick={() => nehmen(w.id)}
            >
              <span className="sf-wahl-name">{w.name}</span>
              <span className="sf-wahl-zahl">{w.zahl}</span>
              <span className="sf-wahl-haken">{gewaehlt === w.id ? <Zeichen name="haken" /> : null}</span>
            </button>
          ))}
        </Zeilenblatt>
      </Blattvonunten>
    </div>
  );
}
